/**
 * The one piece of markup the help content is allowed to carry: **bold**.
 *
 * The content is data rather than HTML so it can be rendered in more than one
 * place, but a few phrases genuinely need weight — the name of a button you are
 * being told to press, or the term a paragraph is defining. A whole markdown
 * dependency for that would be the tail wagging the dog, and rendering the
 * strings as HTML would put an injection hole in a screen that does not need one.
 *
 * So: split on the delimiter, alternate plain and strong. Returns segments
 * rather than elements, keeping this pure and unit-testable.
 */

export type Segment = { text: string; strong: boolean }

/**
 * Split a string into plain and bold segments.
 *
 * An unmatched `**` is treated as literal text rather than opening a run to the
 * end of the paragraph: a typo in the content should look like a typo, not
 * silently embolden everything after it.
 */
export function segments(body: string): Segment[] {
  const parts = body.split('**')

  // An even number of delimiters produces an odd number of parts. Anything else
  // means one is unmatched, so the string is returned as written.
  if (parts.length % 2 === 0) return [{ text: body, strong: false }]

  return parts
    .map((text, i) => ({ text, strong: i % 2 === 1 }))
    .filter((segment) => segment.text.length > 0)
}
